import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Modal,
  Button,
  Row,
  Col,
  TextField,
  Datepicker,
  Select,
  RadioButton,
  Loading,
} from '@folio/stripes/components';

const ClearanceReportModal = ({
  open,
  onClose,
  onExport,
  libraries,
  isLoading,
}) => {
  const intl = useIntl();
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    libraryId: '',
    staffId: '',
    reportName: intl.formatMessage({
      id: 'ui-users.clearance.report.defaultName',
    }),
  });
  const [exportFormat, setExportFormat] = useState('excel');

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleExport = () => {
    const convertedFilters = { ...filters };
    
    // Convert dates from DD/MM/YYYY to YYYY-MM-DD format
    if (filters.startDate) {
      const parts = filters.startDate.split('/');
      if (parts.length === 3) {
        convertedFilters.startDate = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    }
    
    if (filters.endDate) {
      const parts = filters.endDate.split('/');
      if (parts.length === 3) {
        convertedFilters.endDate = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    }
    
    onExport(convertedFilters, exportFormat);
  };

  const footer = (
    <div>
      <Button onClick={onClose} disabled={isLoading}>
        <FormattedMessage id="ui-users.cancel" />
      </Button>
      <Button buttonStyle="primary" onClick={handleExport} disabled={isLoading}>
        {isLoading ? (
          <Loading />
        ) : (
          <FormattedMessage id="ui-users.clearance.report.export" />
        )}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      label={<FormattedMessage id="ui-users.clearance.report.title" />}
      footer={footer}
      onClose={onClose}
      size="medium"
    >
      <Row>
        <Col xs={12}>
          <TextField
            label={<FormattedMessage id="ui-users.clearance.report.name" />}
            value={filters.reportName}
            onChange={(e) => handleFilterChange('reportName', e.target.value)}
            required
          />
        </Col>
      </Row>

      <Row>
        <Col xs={6}>
          <Datepicker
            label={
              <FormattedMessage id="ui-users.clearance.report.startDate" />
            }
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            backendDateStandard="YYYY-MM-DD"
            usePortal
          />
        </Col>
        <Col xs={6}>
          <Datepicker
            label={<FormattedMessage id="ui-users.clearance.report.endDate" />}
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            backendDateStandard="YYYY-MM-DD"
            usePortal
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Select
            label={<FormattedMessage id="ui-users.clearance.report.library" />}
            value={filters.libraryId}
            onChange={(e) => handleFilterChange('libraryId', e.target.value)}
            dataOptions={[
              {
                value: '',
                label: intl.formatMessage({
                  id: 'ui-users.clearance.report.allLibraries',
                }),
              },
              ...libraries.map((lib) => ({
                value: lib.id,
                label: intl.formatMessage(
                  { id: `ui-tenant-settings.loclibs.name.${lib.name}` },
                  { defaultMessage: lib.name },
                ),
              })),
            ]}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <fieldset>
            <legend>
              <FormattedMessage id="ui-users.clearance.report.format" />
            </legend>
            <RadioButton
              label={
                <FormattedMessage id="ui-users.clearance.report.format.excel" />
              }
              checked={exportFormat === 'excel'}
              onChange={() => setExportFormat('excel')}
              name="exportFormat"
            />
            <RadioButton
              label={
                <FormattedMessage id="ui-users.clearance.report.format.pdf" />
              }
              checked={exportFormat === 'pdf'}
              onChange={() => setExportFormat('pdf')}
              name="exportFormat"
            />
          </fieldset>
        </Col>
      </Row>
    </Modal>
  );
};

ClearanceReportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  libraries: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
};

ClearanceReportModal.defaultProps = {
  libraries: [],
  isLoading: false,
};

export default ClearanceReportModal;
